import {
  isAWSExternalActionRequest,
  isBlazeMeterExternalActionRequest
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { ExternalActionPreviewSentinels } from 'types/api/reliability-tests/preview'
import {
  type ExternalActionRequest,
  ExternalActionRequestType
} from 'types/api/scenarios/scenario'
import {
  getAWSAutoscalingGroupNameFromASGArn,
  getAWSLoadBalancerNameFromLoadBalancerArn
} from 'utils/status-checks/status-check-integrations-util-aws'

export enum NodeSectionType {
  ExternalActions = 'ExternalActions',
  ExternalRollbacks = 'ExternalRollbacks',
  Observers = 'Observers',
  Nodes = 'Nodes'
}

const externalActionSentinelValues = new Set<string>(
  Object.values(ExternalActionPreviewSentinels)
)

export const NODE_SECTION_LABELS = {
  [NodeSectionType.ExternalActions]: 'External Steps',
  [NodeSectionType.ExternalRollbacks]: 'External Rollback Steps',
  [NodeSectionType.Observers]: 'Health Checks',
  [NodeSectionType.Nodes]: 'Gremlin Steps'
}

export function getExternalActionText(
  externalActionRequestType: ExternalActionRequestType
) {
  switch (externalActionRequestType) {
    case ExternalActionRequestType.DisableAWSAutoScalingGroups:
      return 'Disable'
    case ExternalActionRequestType.RemoveAWSSecurityGroups:
      return 'Remove'
    case ExternalActionRequestType.RunBlazeMeterTest:
      return 'Start'
    default:
      return null
  }
}

export function getExternalRollbackActionText(
  externalActionRequestType: ExternalActionRequestType
): string | null {
  switch (externalActionRequestType) {
    case ExternalActionRequestType.DisableAWSAutoScalingGroups:
    case ExternalActionRequestType.RemoveAWSSecurityGroups:
      return 'Re-enable'
    case ExternalActionRequestType.NoOp:
      return null
    case ExternalActionRequestType.RunBlazeMeterTest:
      return 'Stop'
  }
}

export function getExternalActionTargetPrefix(
  externalActionRequestType: ExternalActionRequestType,
  includeTransition = true
) {
  switch (externalActionRequestType) {
    case ExternalActionRequestType.DisableAWSAutoScalingGroups:
      return `ASG autoscaling${includeTransition ? ' on' : ''}`
    case ExternalActionRequestType.RemoveAWSSecurityGroups:
      return `Security Group${includeTransition ? ' from' : ''}`
    case ExternalActionRequestType.RunBlazeMeterTest:
      return 'BlazeMeter test'
    default:
      return null
  }
}

export function getResourceNameFromExternalActionRequest(
  externalActionRequest: ExternalActionRequest
) {
  if (isAWSExternalActionRequest(externalActionRequest)) {
    const { resourceArn, type } = externalActionRequest

    if (externalActionSentinelValues.has(resourceArn)) {
      return resourceArn
    }

    return type === ExternalActionRequestType.RemoveAWSSecurityGroups
      ? getAWSLoadBalancerNameFromLoadBalancerArn(resourceArn)
      : getAWSAutoscalingGroupNameFromASGArn(resourceArn)
  }

  if (isBlazeMeterExternalActionRequest(externalActionRequest)) {
    return externalActionRequest.generatorName
  }

  return null
}
